import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import type { DiceRollPayload } from "../shared/messages";
import type { CreatureExperience, PathADiceFace } from "../shared/diceRoll";

export function PathADiceTray(props: {
  roll: DiceRollPayload;
  onClose: () => void;
}) {
  const { roll, onClose } = props;
  const [faces, setFaces] = useState<PathADiceFace[]>(() =>
    roll.faces.map((f) => ({ ...f, value: 0 })),
  );
  const [settled, setSettled] = useState(roll.faces.length === 0);

  useEffect(() => {
    if (roll.faces.length === 0) {
      setFaces([]);
      setSettled(true);
      return;
    }
    setSettled(false);
    let tick = 0;
    const id = window.setInterval(() => {
      tick += 1;
      if (tick >= 12) {
        window.clearInterval(id);
        setFaces(roll.faces);
        setSettled(true);
        return;
      }
      setFaces(
        roll.faces.map((f) => ({
          ...f,
          value:
            f.sides > 0
              ? Math.floor(Math.random() * f.sides) + 1
              : f.value,
        })),
      );
    }, 55);
    return () => window.clearInterval(id);
  }, [roll]);

  return createPortal(
    <div
      className="path-a-dice-tray"
      role="dialog"
      aria-label={`${roll.abilityName} roll`}
      data-testid="path-a-dice-tray"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="path-a-dice-tray__head">
        <div className="path-a-dice-tray__title">
          {roll.abilityName} · {roll.tokenName}
        </div>
        <button
          type="button"
          className="path-a-dice-tray__close"
          aria-label="Close dice tray"
          onClick={onClose}
        >
          ×
        </button>
      </div>
      <div className="path-a-dice-tray__formula">{roll.formula}</div>
      {faces.length > 0 ? (
        <div className="path-a-dice-tray__dice">
          {faces.map((face, i) => (
            <div
              key={`${face.label}-${i}`}
              className={`path-a-dice-tray__die${settled ? "" : " is-rolling"}`}
            >
              <div className="path-a-dice-tray__die-value">{face.value}</div>
              <div className="path-a-dice-tray__die-label">{face.label}</div>
            </div>
          ))}
        </div>
      ) : null}
      {roll.modifier !== 0 || roll.extraModLabel ? (
        <div className="path-a-dice-tray__mods">
          {[
            roll.modifier !== 0
              ? roll.modifier > 0
                ? `+${roll.modifier}`
                : String(roll.modifier)
              : null,
            roll.extraModLabel,
          ]
            .filter(Boolean)
            .join(" · ")}
        </div>
      ) : null}
      {settled && roll.higher ? (
        <div
          className={`path-a-dice-tray__outcome${
            roll.higher === "Fear" ? " is-fear" : ""
          }`}
        >
          Rolled with {roll.higher}
        </div>
      ) : null}
      <div className={`path-a-dice-tray__total${settled ? "" : " is-rolling"}`}>
        {settled ? roll.total : "…"}
      </div>
    </div>,
    document.body,
  );
}

export function DualityOptionsModal(props: {
  traitName: string;
  modifier: number;
  experiences: CreatureExperience[];
  onCancel: () => void;
  onConfirm: (choice: {
    advantage: boolean;
    disadvantage: boolean;
    experience: CreatureExperience | null;
  }) => void;
}) {
  const { traitName, modifier, experiences, onCancel, onConfirm } = props;
  return (
    <div className="dh-duality-modal-backdrop" onClick={onCancel}>
      <div
        className="dh-duality-modal"
        role="dialog"
        aria-label="Daggerheart Duality Roll"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="dh-duality-modal-header">
          <h4>
            Duality Roll: {traitName} (
            {modifier >= 0 ? `+${modifier}` : modifier})
          </h4>
          <button type="button" className="path-a-statblock-close-btn" onClick={onCancel}>
            ×
          </button>
        </div>
        <form
          className="dh-duality-modal-body"
          onSubmit={(e) => {
            e.preventDefault();
            const form = e.currentTarget;
            const adv = String(new FormData(form).get("dh-duality-adv") || "none");
            const expRaw = String(new FormData(form).get("dh-duality-exp") || "");
            let experience: CreatureExperience | null = null;
            if (expRaw === "custom") {
              const cName =
                String(new FormData(form).get("dh-custom-name") || "").trim() ||
                "Experience";
              const cMod = parseInt(String(new FormData(form).get("dh-custom-mod") || "0"), 10) || 0;
              experience = {
                name: cName,
                modifier: cMod,
                text: `${cName} ${cMod >= 0 ? `+${cMod}` : cMod}`,
              };
            } else if (expRaw !== "") {
              const idx = parseInt(expRaw, 10);
              experience = experiences[idx] ?? null;
            }
            onConfirm({
              advantage: adv === "adv",
              disadvantage: adv === "dis",
              experience,
            });
          }}
        >
          <div className="dh-duality-section">
            <div className="dh-duality-section-title">Advantage / Disadvantage</div>
            <div className="dh-duality-choices">
              <label className="dh-duality-radio">
                <input type="radio" name="dh-duality-adv" value="none" defaultChecked />{" "}
                Normal (None)
              </label>
              <label className="dh-duality-radio">
                <input type="radio" name="dh-duality-adv" value="adv" /> Advantage (+1d6)
              </label>
              <label className="dh-duality-radio">
                <input type="radio" name="dh-duality-adv" value="dis" /> Disadvantage (-1d6)
              </label>
            </div>
          </div>
          <div className="dh-duality-section">
            <div className="dh-duality-section-title">Add Experience</div>
            <select name="dh-duality-exp" className="dh-duality-select" defaultValue="">
              <option value="">None</option>
              {experiences.map((exp, i) => (
                <option key={`${exp.text}-${i}`} value={String(i)}>
                  {exp.text}
                </option>
              ))}
              <option value="custom">+ Custom Experience…</option>
            </select>
            <div className="dh-duality-custom-row">
              <input
                type="text"
                name="dh-custom-name"
                placeholder="Experience name"
                className="dh-duality-input"
              />
              <input
                type="number"
                name="dh-custom-mod"
                defaultValue={2}
                className="dh-duality-input"
              />
            </div>
          </div>
          <div className="dh-duality-modal-footer">
            <button type="button" className="dh-pc-btn" onClick={onCancel}>
              Cancel
            </button>
            <button type="submit" className="dh-pc-btn mod-cta">
              Roll Duality
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
